/**
 * CessioneFields — F-004 Phase 3 (IMPL-P3-005).
 *
 * Field group per la variante CESSIONE (trasferimento del contratto a un nuovo
 * inquilino). Tre blocchi:
 *  1. Inquilino uscente — read-only (plain text/`<dl>`), dai dati precompilati
 *     `conduttoreUscente` della pratica madre. Nessun controllo di form
 *     editabile: la parte uscente non si modifica.
 *  2. Nuovo inquilino — riuso IN BLOCCO di `ConduttoreFields` sotto il path
 *     `nuovoInquilino.*` (vedi risoluzione della domanda namePrefix qui sotto).
 *  3. dataDecorrenza — Input type="date", `min` = dataStipulaOriginale.
 *
 * --- Risoluzione della domanda "namePrefix" (IMPL-P3-005) ---
 * `ConduttoreFields` NON accettava un prop di prefix: hardcodava i path
 * `conduttore.*`, gli id DOM `conduttore-*` e la lookup `errors.conduttore`.
 * Opzione scelta: **opzione 2 (prop additiva)**. È stato aggiunto a
 * `ConduttoreFields` un prop opzionale `namePrefix` con default `'conduttore'`;
 * qui lo si passa come `'nuovoInquilino'`. I chiamanti F-003/F-005 esistenti
 * (`ContractWizard`, `ContractDenseForm`, correction flow) non passano il prop
 * e mantengono comportamento identico (stessi path, id e lookup errori). La
 * validazione CF in tempo reale del nuovo inquilino arriva gratis dal riuso di
 * `CFInput` dentro `ConduttoreFields`. Nessun componente duplicato, nessun
 * adapter con `useForm` locale.
 *
 * Pattern seguiti (plan IMPL-P3-005):
 *  - ConduttoreFields.tsx (party field group ARIA-wired, CF-validated).
 *  - wizard/PartyCard.tsx → qui usiamo direttamente `Card` per il wrapper.
 */
import { Controller, useFormContext } from 'react-hook-form';
import type { AdempimentoDraft } from '@app/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConduttoreFields } from '@/features/pratica/components/fields/ConduttoreFields';

export interface ConduttoreUscenteView {
  nome?: string;
  cognome?: string;
  codiceFiscale?: string;
}

export interface CessioneFieldsProps {
  /**
   * Dati (read-only) dell'inquilino uscente, precompilati dalla pratica madre
   * (`conduttoreUscente`). Renderizzati come testo, MAI come input editabili.
   */
  conduttoreUscente?: ConduttoreUscenteView;
  /**
   * Data minima (ISO `YYYY-MM-DD`) per `dataDecorrenza` — la data di stipula
   * originale del contratto madre. Se assente, nessun `min`.
   */
  dataStipulaMinISO?: string;
}

/** Normalizza un valore RHF (Date | stringa ISO | undefined) a `YYYY-MM-DD`. */
function toDateInputValue(raw: unknown): string {
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  if (typeof raw === 'string') return raw.slice(0, 10);
  return '';
}

function formatNome(u?: ConduttoreUscenteView): string {
  if (!u) return '—';
  const full = [u.nome, u.cognome].filter(Boolean).join(' ').trim();
  return full || '—';
}

export function CessioneFields({ conduttoreUscente, dataStipulaMinISO }: CessioneFieldsProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<AdempimentoDraft>();

  const cessioneErrors = errors as Record<string, { message?: string } | undefined>;

  return (
    <div className="space-y-6">
      {/* 1. Inquilino uscente — read-only */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Inquilino uscente</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-2 sm:block">
              <dt className="font-medium text-muted-foreground">Nominativo</dt>
              <dd data-testid="cessione-uscente-nome">{formatNome(conduttoreUscente)}</dd>
            </div>
            <div className="flex justify-between gap-2 sm:block">
              <dt className="font-medium text-muted-foreground">Codice fiscale</dt>
              <dd className="font-mono" data-testid="cessione-uscente-cf">
                {conduttoreUscente?.codiceFiscale ?? '—'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* 2. Nuovo inquilino — riuso in blocco di ConduttoreFields sotto nuovoInquilino.* */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Nuovo inquilino</CardTitle>
        </CardHeader>
        <CardContent>
          <ConduttoreFields namePrefix="nuovoInquilino" />
        </CardContent>
      </Card>

      {/* 3. Data decorrenza cessione */}
      <div className="space-y-1">
        <Label htmlFor="data-decorrenza">Data di decorrenza *</Label>
        <Controller
          name="dataDecorrenza"
          control={control}
          render={({ field }) => (
            <Input
              id="data-decorrenza"
              type="date"
              min={dataStipulaMinISO}
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              value={toDateInputValue(field.value)}
              onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : undefined)}
              aria-invalid={!!cessioneErrors.dataDecorrenza}
              aria-describedby={cessioneErrors.dataDecorrenza ? 'data-decorrenza-error' : undefined}
            />
          )}
        />
        {cessioneErrors.dataDecorrenza && (
          <p id="data-decorrenza-error" className="text-xs text-destructive">
            {cessioneErrors.dataDecorrenza.message}
          </p>
        )}
      </div>
    </div>
  );
}
